import { useHistory } from 'react-router-dom';
import { translate } from '../../lib/lang.helper';
import { useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { Spinner, Subtitle } from '../../components/shared';
import { formatNumber } from '../../entities/util-functions';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { useEffect, useState } from 'react';
import { Table } from 'antd';
import { calcLineLastSize } from '../../lib/farm.helpers';
import { sendSingleRequest } from '../../apis';
import { ChartData } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  calcDiffDays,
  generateMonthlyRanges,
  generateWeeklyRanges,
  pcsToDoz,
  defaultDateFormat,
  toMillisecond,
  amountDays,
  displayValueUnit,
} from '../../lib/common.helpers';
import { OysterSummaryTabs } from './OysterSummaryPage';
import { IOysterLineResource } from '../../entities/farms.entities';
import { selectAccount } from '../../store/auth/auth.selector';

interface ILineProp {
  farm_id: number;
  line_id: number;
  predicted_harvest_date: number;
  predicted_harvest_amount: number;
}

interface IRow {
  id: number | string;
  week: string;
  date: string;
  farm_id?: number;
  farm_name: string;
  farm_number: string;
  line_id?: number;
  line: string;
  farming_method: string;
  last_date: string;
  last_size: string;
  basket_count?: number;
  age: string;
  last_harvest: string;
  estimate_dz: string;
  rowSpan?: number;
  total?: number;
}

const columns = (lang: any) => [
  {
    key: 'week',
    render: (x: IRow) =>
      x.rowSpan ? (
        <span>
          <div className='text-center'>{x.week}</div>
          <div>{x.date}</div>
        </span>
      ) : (
        ''
      ),
    width: 210,
  },
  {
    title: translate(lang, 'Farm'),
    key: 'farm',
    render: (x: IRow) => (
      <span>
        <div>{x.farm_name}</div>
        <div className='text-center'>{x.farm_number}</div>
      </span>
    ),
  },
  {
    title: translate(lang, 'Line'),
    key: 'line',
    render: (x: IRow) => <span>{x.line}</span>,
    width: 80,
  },
  {
    title: translate(lang, 'Last assessment date'),
    key: 'last_date',
    render: (x: IRow) => <span>{x.last_date}</span>,
  },
  {
    title: translate(lang, 'Last known size'),
    key: 'last_size',
    render: (x: IRow) => <span>{x.last_size}</span>,
  },
  {
    title: translate(lang, 'Number of containers'),
    key: 'line_length',
    render: (x: IRow) =>
      x.basket_count ? (
        <span>{`${x.basket_count} ${x.farming_method}`}</span>
      ) : (
        <></>
      ),
  },
  {
    title: translate(lang, "Today's age"),
    key: 'age',
    render: (x: IRow) => <span>{x.age}</span>,
  },
  {
    title: translate(lang, 'Previous harvest'),
    key: 'last_harvest',
    render: (x: IRow) => <span>{x.last_harvest}</span>,
  },
  {
    title: translate(lang, 'Estimated harvest'),
    key: 'estimate_harvest',
    render: (x: IRow) => <span>{x.estimate_dz}</span>,
  },
  {
    title: translate(lang, 'Total'),
    key: 'total',
    render: (x: IRow) => (
      <span>{x.total ? `${formatNumber(x.total)} dz` : ''}</span>
    ),
  },
];

const OysterCalenderPage = () => {
  const history = useHistory();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [rowsData, setRowsData] = useState<IRow[]>();
  const [chartData, setChartData] =
    useState<ChartData<'bar', number[], string>>();

  const totalSum = rowsData?.reduce((p, c) => p + (c.total ?? 0), 0) ?? 0;

  useEffect(() => {
    (async () => {
      const response = await sendSingleRequest(
        {},
        'GET',
        'api/farm/lines-props',
        true,
      );
      if (!response.status) {
        window.alert(response.data?.message ?? 'Server error');
        return;
      }
      const linesProps: ILineProp[] = response.data;
      const weekRanges = generateWeeklyRanges();
      let result: IRow[] = [];
      for (const week of weekRanges) {
        let first = 0,
          cnt = 0,
          totalSum = 0;
        for (const farm of farmsData) {
          for (const il of farm.lines) {
            if (il.crop_type !== 'OYSTER') continue;
            const line = il as IOysterLineResource;
            const cycle = line.growing_cycle;
            if (!cycle) continue;

            const prop = linesProps.find(
              x => x.farm_id === farm.id && x.line_id === line.id,
            );
            if (!prop) continue;

            const shellSize = calcLineLastSize(line);
            if (!shellSize) continue;

            if (
              week.start_date <= toMillisecond(prop.predicted_harvest_date) &&
              toMillisecond(prop.predicted_harvest_date) <= week.finish_date
            ) {
              const id = result.length + 1;
              let lastHarvestStr = '-';
              if (line.last_final_harvest) {
                lastHarvestStr = `${formatNumber(
                  pcsToDoz(line.last_final_harvest.amount),
                )} dz${
                  line.last_final_harvest.line_length
                    ? ` (${formatNumber(line.last_final_harvest.line_length)} ${
                        line.farming_method ?? 'baskets'
                      })`
                    : ''
                }`;
              }
              result.push({
                id: id,
                week: week.week_label,
                date: week.day_label,
                farm_id: farm.id,
                line_id: line.id,
                farm_name: farm.name,
                farm_number: farm.farm_number,
                line: line.line_name,
                last_date:
                  defaultDateFormat(cycle.last_assessment?.assessment_date) ??
                  '-',
                last_size: displayValueUnit(shellSize.avg, unitType, 'size_mm'),
                basket_count: cycle.current_basket_count,
                age: amountDays(
                  calcDiffDays(cycle.main_seed.planned_date_seed),
                  lang,
                ),
                last_harvest: lastHarvestStr,
                estimate_dz: `${pcsToDoz(prop.predicted_harvest_amount)} dz`,
                farming_method: line.farming_method ?? 'baskets',
              });
              if (!first) first = id;
              totalSum += pcsToDoz(prop.predicted_harvest_amount);
              cnt++;
            }
          }
        }
        if (first) {
          let i = result.findIndex(x => x.id === first);
          result[i].rowSpan = cnt;
          result[result.length - 1].total = totalSum;
        } else {
          result.push({
            id: result.length + 1,
            week: week.week_label,
            date: week.day_label,
            farm_name: '',
            farm_number: '',
            line: '',
            last_date: '',
            last_size: '',
            basket_count: undefined,
            age: '',
            last_harvest: '',
            estimate_dz: '',
            farming_method: '',
            rowSpan: 1,
          });
        }
      }
      setRowsData(result);

      const monthRanges = generateMonthlyRanges();
      let chartValues: number[] = [];
      let chartLabels: string[] = [];
      for (const month of monthRanges) {
        let sum = 0;
        for (const farm of farmsData) {
          for (const line of farm.lines) {
            if (!line.growing_cycle) continue;
            const shellSize = calcLineLastSize(line);
            if (!shellSize) continue;

            const prop = linesProps.find(
              x => x.farm_id === farm.id && x.line_id === line.id,
            );
            if (!prop) continue;

            if (
              month.start_date <= toMillisecond(prop.predicted_harvest_date) &&
              toMillisecond(prop.predicted_harvest_date) <= month.finish_date
            ) {
              sum += pcsToDoz(prop.predicted_harvest_amount);
            }
          }
        }
        chartLabels.push(month.month_label);
        chartValues.push(sum);
      }
      setChartData({
        labels: chartLabels,
        datasets: [
          {
            data: chartValues,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
            label: translate(lang, 'Estimated Harvest'),
          },
        ],
      });
    })();
  }, []);

  return (
    <div className='bg-secondary'>
      <div
        className='container performance-page pb-32'
        style={{ maxWidth: 1600 }}
      >
        <OysterSummaryTabs />
        {rowsData === undefined ? (
          <div className='loader'>
            <Spinner />
          </div>
        ) : (
          <div className='content pb-32'>
            {chartData !== undefined && (
              <div className='mb-24 pr-16 pl-16 pt-16 pb-16'>
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                      },
                    },
                  }}
                  height={70}
                />
              </div>
            )}
            <Table
              rowKey={'id'}
              columns={columns(lang)}
              dataSource={rowsData}
              pagination={false}
              onRow={row => ({
                onClick: () =>
                  row.farm_id && row.line
                    ? history.push(`/farms/${row.farm_id}/${row.line_id}`)
                    : undefined,
                style: { cursor: 'pointer' },
              })}
              scroll={{ y: 600 }}
            />
            <div className='mr-32 mt-16 mb-7'>
              <Subtitle size={4} color='black' align='right' fontWeight={500}>
                {`${translate(lang, 'Total')}: ${formatNumber(totalSum)} dz`}
              </Subtitle>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OysterCalenderPage;
